import type { GameState } from '../game/GameState.js';
import type { Move } from '../game/types.js';
import type { Difficulty } from '../settings/types.js';
import type { BoardEvaluator } from './BoardEvaluator.js';
import type { MinimaxSearch } from './MinimaxSearch.js';
import type { MoveEnumerator } from './MoveEnumerator.js';

export interface DifficultyDependencies {
  readonly enumerator: MoveEnumerator;
  readonly evaluator: BoardEvaluator;
  readonly search: MinimaxSearch;
  /**
   * Source of randomness (seedable for tests). Yields a value in [0, 1).
   * Defaults to `Math.random` when absent.
   */
  readonly rng?: () => number;
}

export interface MoveChoice {
  readonly move: Move;
  /** Heuristic score from the AI's perspective, when available. */
  readonly score: number | null;
  /** Deepest search depth fully completed (0 for non-search tiers). */
  readonly depth: number;
}

/**
 * Translates a `Difficulty` setting into a concrete move selection. Each tier
 * is a distinct strategy rather than a parameter on the same algorithm — that
 * matches what a player intuitively expects from "Easy" vs "Expert" and keeps
 * the easy tier fast (no search, no eval).
 *
 *  - **easy**: uniform-random among legal moves. Captures are still mandatory
 *    (the validator filters), so easy still plays the rules; it just doesn't
 *    discriminate between them.
 *  - **normal**: depth-1 search — evaluate every legal move's immediate
 *    successor, pick the best. Effectively greedy with capture quiescence.
 *  - **hard**: depth-4 alpha-beta with quiescence and the transposition table.
 *  - **expert**: iterative deepening up to depth-8 with a 1.2s soft budget;
 *    falls back to whatever depth completed when the budget elapses.
 */
export class DifficultyStrategy {
  constructor(private readonly deps: DifficultyDependencies) {}

  pickMove(state: GameState, difficulty: Difficulty): MoveChoice | null {
    switch (difficulty) {
      case 'easy':
        return this.pickEasy(state);
      case 'normal':
        return this.pickNormal(state);
      case 'hard':
        return this.pickWithSearch(state, { maxDepth: 4 });
      case 'expert':
        return this.pickWithSearch(state, {
          maxDepth: 8,
          timeBudgetMs: 1_200,
          iterativeDeepening: true,
        });
    }
  }

  private pickEasy(state: GameState): MoveChoice | null {
    const moves = this.deps.enumerator.enumerate(state);
    const rng = this.deps.rng ?? Math.random;
    const idx = Math.floor(rng() * moves.length);
    const move = moves[Math.min(idx, moves.length - 1)];
    if (!move) return null;
    return { move, score: null, depth: 0 };
  }

  private pickNormal(state: GameState): MoveChoice | null {
    const moves = this.deps.enumerator.enumerate(state);
    const firstMove = moves[0];
    if (!firstMove) return null;

    let bestMove = firstMove;
    let bestScore = Number.NEGATIVE_INFINITY;
    for (const m of moves) {
      const child = state.applyMove(m);
      // From the mover's perspective: child's currentPlayer is the opponent,
      // so we evaluate from `state.currentPlayer` and negate the opponent's view.
      const score = this.deps.evaluator.evaluate(child, state.currentPlayer);
      if (score > bestScore) {
        bestScore = score;
        bestMove = m;
      }
    }
    return { move: bestMove, score: bestScore, depth: 1 };
  }

  private pickWithSearch(
    state: GameState,
    opts: { maxDepth: number; timeBudgetMs?: number; iterativeDeepening?: boolean },
  ): MoveChoice | null {
    const result = this.deps.search.search(state, opts);
    if (!result) return null;
    return { move: result.move, score: result.score, depth: result.depthReached };
  }
}
